import { RRule } from 'rrule'

/**
 * Tasks that come back.
 *
 * The bin goes out every Tuesday, the car is inspected every year, the extractor
 * filters every quarter. Those are exactly the things a household forgets,
 * because what returns regularly is what leaves your head.
 *
 * Rule 19: use an RRULE library, never hand-roll one. That happens here — the
 * `rrule` package is already in the bundle for the recurring bills, and this
 * reuses it rather than inventing a second notion of "every week".
 *
 * The shapes are a fixed short list, not an RRULE editor. An RRULE editor is a
 * thing nobody has ever enjoyed using, and a household needs about six.
 */
export interface TaskCadence {
  key: string
  label: string
  /** Built from the date the task is due, so "every week" means that weekday. */
  build: (due: Date) => string
}

const WEEKDAY_RRULE = [
  RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR, RRule.SA, RRule.SU,
]

/** Monday = 0, matching the Dutch week and WEEKDAY_RRULE above. */
function weekdayIndex(d: Date): number {
  return (d.getDay() + 6) % 7
}

export const TASK_CADENCES: TaskCadence[] = [
  { key: 'daily', label: 'Elke dag', build: () => 'FREQ=DAILY' },
  {
    key: 'weekly',
    label: 'Elke week',
    build: (d) => `FREQ=WEEKLY;BYDAY=${WEEKDAY_RRULE[weekdayIndex(d)]!.toString()}`,
  },
  {
    key: 'fortnightly',
    label: 'Om de twee weken',
    build: (d) => `FREQ=WEEKLY;INTERVAL=2;BYDAY=${WEEKDAY_RRULE[weekdayIndex(d)]!.toString()}`,
  },
  { key: 'monthly', label: 'Elke maand', build: (d) => `FREQ=MONTHLY;BYMONTHDAY=${d.getDate()}` },
  {
    key: 'quarterly',
    label: 'Elk kwartaal',
    build: (d) => `FREQ=MONTHLY;INTERVAL=3;BYMONTHDAY=${d.getDate()}`,
  },
  {
    key: 'yearly',
    label: 'Elk jaar',
    // BYMONTH is not decoration. Without it RRULE reads a yearly rule as "that
    // day of every month" — twelve times a year. See CADENCES in recurring.ts,
    // where the same omission was a money bug.
    build: (d) => `FREQ=YEARLY;BYMONTH=${d.getMonth() + 1};BYMONTHDAY=${d.getDate()}`,
  },
]

const DAY_NAMES = ['maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag', 'zondag']

/** What the stored rule means, in the words someone would say. */
export function describeRepeat(rule: string | null): string | null {
  if (!rule) return null
  const day = /BYDAY=([A-Z]{2})/.exec(rule)?.[1]
  const name = day ? DAY_NAMES[['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'].indexOf(day)] : null
  const dom = /BYMONTHDAY=(\d+)/.exec(rule)?.[1]

  if (rule.startsWith('FREQ=DAILY')) return 'elke dag'
  if (rule.startsWith('FREQ=WEEKLY')) {
    const every = rule.includes('INTERVAL=2') ? 'om de twee weken' : 'elke week'
    return name ? `${every} op ${name}` : every
  }
  if (rule.startsWith('FREQ=YEARLY')) return dom ? `elk jaar op de ${dom}e` : 'elk jaar'  // eslint-disable-line
  if (rule.includes('INTERVAL=3')) return dom ? `elk kwartaal op de ${dom}e` : 'elk kwartaal'
  if (rule.startsWith('FREQ=MONTHLY')) return dom ? `elke maand op de ${dom}e` : 'elke maand'
  return 'herhaalt'
}

/**
 * The next time this comes round, after the one just ticked off.
 *
 * Counted from the due date rather than from today, so a bin put out two days
 * late still returns next Tuesday and not next Thursday — the rhythm belongs to
 * the chore, not to when you got to it.
 *
 * Returns null on a rule the library refuses, which is deliberately survivable:
 * the task is still marked done, there is simply no next one, and nobody loses
 * anything they cannot re-enter.
 */
export function nextOccurrence(rule: string, dueDate: string | null): string | null {
  // Everything here happens at noon UTC, start to finish.
  //
  // The first version built `from` with `new Date(iso + 'T12:00:00')`, which a
  // browser reads in **local** time — 10:00 UTC in Belgium — while DTSTART was
  // noon UTC. `after()` then found the start itself still ahead and handed back
  // the very same day, so ticking off a weekly chore created a duplicate for
  // today. It passed every test, because the tests ran in UTC where the two
  // happened to coincide. Same frame on both sides, or no frame at all.
  const from = dueDate ? utcNoon(dueDate) : utcNoon(isoToday())
  try {
    const set = RRule.fromString(`${rule};DTSTART=${stamp(from)}`)
    const next = set.after(from, false)
    return next ? isoDate(next) : null
  } catch {
    return null
  }
}

function utcNoon(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number]
  return new Date(Date.UTC(y, m - 1, d, 12))
}

/** Today on the local calendar — the day the person is in, not UTC's. */
function isoToday(): string {
  const now = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

function stamp(d: Date): string {
  return d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')
}

function isoDate(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(
    d.getUTCDate(),
  ).padStart(2, '0')}`
}
